import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Button, Row, Col, Statistic, message, Spin, Empty, Image, Typography, Space } from 'antd';
import { EditOutlined, SaveOutlined, CalculatorOutlined } from '@ant-design/icons';
import axios from 'axios';
import UniversalModuleModal from './UniversalModuleModal';
import { MODULE_MODAL_CONFIGS } from './moduleModalConfigs';

const { Text, Title, Paragraph } = Typography;

const TimeSeriesAnalysis = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
    const [activeTab, setActiveTab] = useState('input');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);

    // Data states
    const [demand, setDemand] = useState([]);
    const [method, setMethod] = useState('naive');
    const [params, setParams] = useState({});
    const [results, setResults] = useState(null);
    const [chartUrl, setChartUrl] = useState(null);

    const getAuthToken = () => localStorage.getItem('access_token');

    useEffect(() => {
        if (fileId) {
            loadFileData(fileId);
        } else if (initialConfig) {
            setDemand(initialConfig.demand || []);
            setMethod(initialConfig.method || 'naive');
            setParams(initialConfig.params || {});
        }
    }, [fileId, initialConfig]);

    const loadFileData = async (id) => {
        try {
            const token = getAuthToken();
            if (!token) return;

            setIsLoading(true);
            const response = await axios.get(`/api/retrieve_timeSeriesAnalysis/${id}/`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const { input_data, output_data, chart_url } = response.data;
            if (input_data) {
                setDemand(input_data.demand || []);
                setMethod(input_data.method || 'naive');
                setParams(input_data.params || {});
            }

            if (output_data) {
                setResults(output_data);
                setChartUrl(chart_url);
                setActiveTab('output');
            }

            setIsSaved(true);
            setIsLoading(false);
        } catch (error) {
            console.error('Error loading file:', error);
            message.error('Failed to load analysis data');
            setIsLoading(false);
        }
    };

    const saveData = async (modalConfig = null) => {
        try {
            const token = getAuthToken();
            if (!token) {
                message.error("User is not authenticated.");
                return;
            }

            setIsLoading(true);

            const currentDemand = modalConfig ? modalConfig.demand : demand;
            const currentMethod = modalConfig ? modalConfig.method : method;
            const currentParams = modalConfig ? modalConfig.params : params;

            const payload = {
                name: modalConfig ? modalConfig.name : (fileName || 'Time Series Analysis'),
                input_data: {
                    method: currentMethod,
                    demand: currentDemand,
                    params: currentParams
                }
            };

            const headers = { Authorization: `Bearer ${token}` };
            let response;

            if (fileId) {
                response = await axios.put(`/api/update_timeSeriesAnalysis/${fileId}/`, payload, { headers });
            } else {
                response = await axios.post('/api/save_timeSeriesAnalysis/', payload, { headers });
            }

            const { output_data, chart_url } = response.data;
            setResults(output_data);
            setChartUrl(chart_url);

            if (modalConfig) {
                setDemand(currentDemand);
                setMethod(currentMethod);
                setParams(currentParams);
            }

            setActiveTab('output');
            setIsSaved(true);
            message.success('Analysis saved and calculated');
            setIsLoading(false);

        } catch (error) {
            console.error('Error saving data:', error);
            const errorMsg = error.response?.data?.error || 'Failed to save analysis';
            message.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
            setIsLoading(false);
        }
    };

    const renderAsciiTable = (tableStr) => {
        if (!tableStr) return null;
        return (
            <div style={{
                overflowX: 'auto',
                backgroundColor: '#f5f5f5',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #d9d9d9',
                fontFamily: 'monospace',
                whiteSpace: 'pre',
                fontSize: '12px',
                marginBottom: '16px'
            }}>
                {tableStr}
            </div>
        );
    };

    const renderInputTab = () => (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <Card
                    title="Forecasting Configuration"
                    size="small"
                    extra={<Button icon={<EditOutlined />} onClick={() => setIsEditModalVisible(true)}>Edit Input</Button>}
                >
                    <Row gutter={16}>
                        <Col span={8}>
                            <Statistic title="Method" value={method.replace(/_/g, ' ').toUpperCase()} valueStyle={{ fontSize: '18px' }} />
                        </Col>
                        <Col span={8}>
                            <Statistic title="Data Points" value={demand.length} />
                        </Col>
                        {params.alpha !== undefined && (
                            <Col span={4}>
                                <Statistic title="Alpha" value={params.alpha} precision={2} />
                            </Col>
                        )}
                        {params.beta !== undefined && (
                            <Col span={4}>
                                <Statistic title="Beta" value={params.beta} precision={2} />
                            </Col>
                        )}
                        {params.n !== undefined && (
                            <Col span={4}>
                                <Statistic title="Window (n)" value={params.n} />
                            </Col>
                        )}
                    </Row>
                </Card>
            </Col>
            <Col span={24}>
                <Table
                    dataSource={demand.map((v, i) => ({ key: i, period: i + 1, value: v }))}
                    columns={[
                        { title: 'Period', dataIndex: 'period', key: 'period' },
                        { title: 'Demand', dataIndex: 'value', key: 'value' }
                    ]}
                    pagination={false}
                    size="small"
                    bordered
                    scroll={{ y: 300 }}
                />
            </Col>
            <Col span={24} style={{ textAlign: 'right' }}>
                <Button
                    type="primary"
                    size="large"
                    icon={<CalculatorOutlined />}
                    onClick={() => saveData()}
                    loading={isLoading}
                >
                    Calculate & Save
                </Button>
            </Col>
        </Row>
    );

    const renderOutputTab = () => {
        if (!results) {
            return <Empty description="No results yet. Click 'Calculate & Save' to see results." />;
        }

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                {results.summary && (
                    <Card title="Summary Statistics" size="small">
                        {renderAsciiTable(results.summary)}
                    </Card>
                )}

                {results.detailed_errors && (
                    <Card title="Detailed Error Analysis" size="small">
                        {renderAsciiTable(results.detailed_errors)}
                    </Card>
                )}

                {results.tracking_signal && (
                    <Card title="Tracking Signal & MAD" size="small">
                        {renderAsciiTable(results.tracking_signal)}
                    </Card>
                )}

                {results.n_analysis && (
                    <Card title="Window Size Analysis (Impact of n)" size="small">
                        {renderAsciiTable(results.n_analysis)}
                    </Card>
                )}

                {results.alpha_analysis && (
                    <Card title="Alpha Sensitivity Analysis" size="small">
                        {renderAsciiTable(results.alpha_analysis)}
                    </Card>
                )}

                {chartUrl && (
                    <Card title="Forecasting Chart" size="small">
                        <div style={{ textAlign: 'center' }}>
                            <Image src={chartUrl} alt="Forecasting Chart" style={{ maxWidth: '100%' }} />
                        </div>
                    </Card>
                )}
            </Space>
        );
    };

    return (
        <Card title={fileName || "Time Series Analysis"} className="analysis-card shadow-sm">
            <Spin spinning={isLoading}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: "input",
                            label: "Demand Input",
                            children: renderInputTab()
                        },
                        {
                            key: "output",
                            label: "Forecasting Results",
                            children: renderOutputTab()
                        }
                    ]}
                />
            </Spin>

            <UniversalModuleModal
                isVisible={isEditModalVisible}
                onClose={() => setIsEditModalVisible(false)}
                onConfirm={(config) => {
                    setIsEditModalVisible(false);
                    if (config.numPeriods) {
                        const newCount = config.numPeriods;
                        let newDemand = [...demand];
                        if (newDemand.length < newCount) {
                            while (newDemand.length < newCount) newDemand.push(0);
                        } else if (newDemand.length > newCount) {
                            newDemand = newDemand.slice(0, newCount);
                        }
                        setDemand(newDemand);
                    }
                }}
                config={MODULE_MODAL_CONFIGS.time_series}
                initialValues={{
                    numPeriods: demand.length || 6
                }}
            />
        </Card>
    );
};

export default TimeSeriesAnalysis;
